import { useEffect, useRef } from 'react';
import useTimerStore from '../../stores/useTimerStore';
import useFlowStore from '../../stores/useFlowStore';
import useUserStore from '../../stores/useUserStore';
import { sendNotification } from '../../utils/notify';

// Minimum time threshold — if more than this remains when going idle, it was an abandon
const ABANDON_TIME_THRESHOLD = 1;

// ─── Ritual event trigger helper ───
function fireRitualTrigger(eventType) {
    window.dispatchEvent(new CustomEvent('ritual-trigger', { detail: { eventType } }));
}

// Shared BroadcastChannel for real-time cross-window tick relay.
// Tauri WebviewWindows share the same origin, so BroadcastChannel works.
const timerChannel = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('alphafocus_timer')
    : null;

/**
 * Global timer engine — runs in AppShell so timers keep ticking
 * regardless of which page the user is on. Uses delta time to combat background tab throttling.
 */
export default function TimerEngine() {
    const isRunning = useTimerStore(s => s.isRunning);
    const isPaused = useTimerStore(s => s.isPaused);
    const timeRemaining = useTimerStore(s => s.timeRemaining);
    const phase = useTimerStore(s => s.phase);
    const tick = useTimerStore(s => s.tick);
    const nextPhase = useTimerStore(s => s.nextPhase);

    // Ref to track actual timestamp for accurate delta
    const lastTick = useRef(Date.now());
    const prevPhaseRef = useRef(phase);

    // Freestyle timer tick — runs globally
    useEffect(() => {
        if (!isRunning || isPaused || timeRemaining <= 0) {
            lastTick.current = Date.now();
            return;
        }

        // Reset tracking on mount/resume
        lastTick.current = Date.now();

        const interval = setInterval(() => {
            const now = Date.now();
            const deltaSecs = Math.round((now - lastTick.current) / 1000);

            if (deltaSecs >= 1) {
                lastTick.current = now;
                const s = useTimerStore.getState();
                if (s.timeRemaining > 0) {
                    tick(deltaSecs); // Pass delta to subtract missed seconds (throttle protection)

                    // After tick, check if time has expired — advance phase
                    const afterTick = useTimerStore.getState();
                    if (afterTick.timeRemaining <= 0 && afterTick.isRunning) {
                        nextPhase();
                    }

                    // Broadcast tick to mini-player (real-time, no localStorage delay)
                    timerChannel?.postMessage({
                        type: 'freestyle_tick',
                        timeRemaining: Math.max(0, s.timeRemaining - deltaSecs),
                        totalTime: s.totalTime,
                        phase: s.phase,
                        isPaused: s.isPaused,
                        isRunning: s.isRunning,
                        activeTaskId: s.activeTaskId,
                    });
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isRunning, isPaused, tick, nextPhase]); // deliberately omitting timeRemaining to prevent interval churn

    // ─── Ritual Event Triggers + Notifications — fire on phase transitions ───
    useEffect(() => {
        const prevPhase = prevPhaseRef.current;
        prevPhaseRef.current = phase;

        if (prevPhase === phase) return; // no transition

        const notificationsEnabled = useUserStore.getState().preferences?.notificationsEnabled !== false;

        // pre-focus: entering focus phase (from prep or start)
        if (phase === 'focus' && prevPhase === 'prep') {
            fireRitualTrigger('pre-focus');
        }

        // post-focus: leaving focus phase (to reflect, rest, or done)
        if (prevPhase === 'focus' && (phase === 'reflect' || phase === 'rest' || phase === 'prep')) {
            fireRitualTrigger('post-focus');
            if (notificationsEnabled) {
                sendNotification({
                    title: 'Focus session complete! 🎉',
                    body: 'Great work — time for a break.',
                    tag: 'session-phase',
                });
            }
        }

        // rest → focus: break over
        if (prevPhase === 'rest' && phase === 'focus') {
            if (notificationsEnabled) {
                sendNotification({
                    title: "Break's over ⚡",
                    body: 'Time to focus — let\'s go!',
                    tag: 'session-phase',
                });
            }
        }
    }, [phase]);

    // ─── Flow Event Triggers — fire on flow execution state transitions ───
    const flowExecState = useFlowStore(s => s.executionState);
    const prevFlowExecRef = useRef(flowExecState);

    useEffect(() => {
        const prev = prevFlowExecRef.current;
        prevFlowExecRef.current = flowExecState;

        if (prev === flowExecState) return;

        // pre-flow: flow just started
        if (flowExecState === 'running' && prev === 'idle') {
            fireRitualTrigger('pre-flow');
        }

        // post-flow: flow just completed (not abandoned)
        // If time remains on the flow timer, it was an explicit abandon — don't celebrate
        if (flowExecState === 'idle' && prev === 'running') {
            const flowState = useFlowStore.getState();
            const wasAbandoned = flowState.flowTimeRemaining != null && flowState.flowTimeRemaining > ABANDON_TIME_THRESHOLD;
            if (!wasAbandoned) {
                fireRitualTrigger('post-flow');
                const notificationsEnabled = useUserStore.getState().preferences?.notificationsEnabled !== false;
                if (notificationsEnabled) {
                    sendNotification({
                        title: 'Flow complete! 🏆',
                        body: 'All steps finished — great execution.',
                        tag: 'flow-complete',
                    });
                }
            }
        }
    }, [flowExecState]);

    // Flow timer tick
    const bpExecState = useFlowStore(s => s.executionState);
    const flowTimeRemaining = useFlowStore(s => s.flowTimeRemaining);
    // Track whether flowTimeRemaining is valid to re-trigger the effect
    // when it transitions from null → number on step init
    const bpTimerActive = flowTimeRemaining != null && flowTimeRemaining > 0;
    const flowPhase = useFlowStore(s => s.flowPhase);
    const flowIsPaused = useFlowStore(s => s.flowIsPaused);
    const setFlowTimer = useFlowStore(s => s.setFlowTimer);

    const bpLastTick = useRef(Date.now());

    useEffect(() => {
        if (bpExecState !== 'running' || flowIsPaused || flowTimeRemaining == null || flowTimeRemaining <= 0) {
            bpLastTick.current = Date.now();
            return;
        }

        bpLastTick.current = Date.now();

        const interval = setInterval(() => {
            const s = useFlowStore.getState();
            if (s.flowIsPaused || s.flowTimeRemaining == null || s.flowTimeRemaining <= 0) return;

            const now = Date.now();
            const deltaSecs = Math.round((now - bpLastTick.current) / 1000);

            if (deltaSecs >= 1) {
                bpLastTick.current = now;
                const nextTime = Math.max(0, s.flowTimeRemaining - deltaSecs);
                setFlowTimer(nextTime, s.flowPhase);

                // Broadcast flow tick to mini-player
                timerChannel?.postMessage({
                    type: 'flow_tick',
                    flowTimeRemaining: nextTime,
                    flowPhase: s.flowPhase,
                    flowIsPaused: s.flowIsPaused,
                    currentStepIndex: s.currentStepIndex,
                    totalSteps: s.activeFlow?.steps?.length || 0,
                    flowActiveTaskId: s.flowActiveTaskId,
                });
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [bpExecState, flowIsPaused, flowPhase, setFlowTimer, bpTimerActive]);

    return null; // No UI
}
